import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaExclamationTriangle, FaTrashAlt } from 'react-icons/fa';
import { getDeleteImpact } from './datasourcesApi';
import { getErrorMessage } from 'Utils';

// Pre-delete confirmation modal. Fetches delete-impact counts and shows:
//   • How many discover/masking/encrypt jobs will be cascaded
//   • A prominent warning when the source is currently in an ENCRYPTED /
//     ENCRYPTING state — gated behind a checkbox so the user can't tab past it.
const DeleteConfirmationModal = ({ show, onHide, dataSource, onConfirm }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!show || !dataSource || dataSource.id == null) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      setAcknowledged(false);
      try {
        const dto = await getDeleteImpact(dataSource.id);
        setImpact(dto);
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [show, dataSource]);

  const encryptedWarning = impact && impact.currentlyEncrypted;
  const canConfirm = !loading
    && !error
    && (!encryptedWarning || acknowledged);

  const name = dataSource && dataSource.name;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title><FaTrashAlt className='mr-2' />Delete data source?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='d-flex justify-content-center p-3'>
            <Spinner animation='border' role='status' />
          </div>
        )}
        {error && <Alert variant='danger'>{error}</Alert>}
        {!loading && impact && (
          <>
            <p>
              Delete <strong>{name}</strong>? This permanently removes the data source
              and everything C2 has learned about it:
            </p>
            <ul>
              <li>
                {impact.discoverJobs} discover result{impact.discoverJobs === 1 ? '' : 's'}
                {impact.importedDiscoverJobs > 0 && (
                  <> ({impact.importedDiscoverJobs} imported from{' '}
                    <strong>{impact.registeredServiceId}</strong>)</>
                )}
              </li>
              <li>
                {impact.maskingJobs} masking job record{impact.maskingJobs === 1 ? '' : 's'}
              </li>
              <li>
                {impact.encryptJobs} encrypt/decrypt job record{impact.encryptJobs === 1 ? '' : 's'}
              </li>
            </ul>
            {encryptedWarning && (
              <Alert variant='warning' className='d-flex'>
                <FaExclamationTriangle size={24} className='mr-3 mt-1' />
                <div>
                  <strong>Decrypt before deleting.</strong>{' '}
                  This data source is currently <strong>{impact.encryptionStatus}</strong>.
                  The underlying database will remain encrypted, and C2 will lose the FPE
                  inventory required to reverse the encryption. Deleting is unrecoverable.
                  <Form.Check
                    type='checkbox'
                    className='mt-2'
                    id='delete-ds-encryption-ack'
                    label='I understand and want to proceed anyway.'
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                </div>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button variant='danger' disabled={!canConfirm} onClick={onConfirm}>
          <FaTrashAlt className='mr-2' />Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmationModal;
